import React, { useMemo } from "react";

import { Title, Card } from "shared/components";
import { Spacer } from "shared/layout";
import { ICardTheme, IFormButtons } from "shared/types";
import { IEquipmentSubcategoryItem } from "Venues/types";
import { copy } from "shared/assets";
import { useStagePageContext, useVenuePageContext } from "Venues/contexts";
import { API_ENDPOINTS } from "shared/constants";
import { CreateEquipment } from "../CreateEquipment/CreateEquipment";
import { useDispatch } from "react-redux";
import { dispatchGetStageEquipmentAction } from "Venues/state";

const {
  editEquipment: { addEquipment },
  createEquipment: createEquipmentCopy,
} = copy;

interface EditEquipmentProps {
  category: IEquipmentSubcategoryItem;
  closeForm: () => void;
}

export const EditEquipment: React.FC<EditEquipmentProps> = ({ category, closeForm }) => {
  const { venueId } = useVenuePageContext();
  const { stageId } = useStagePageContext();

  const dispatch = useDispatch();

  const defaultEquipmentValues = useMemo(() => {
    if (!stageId || !category) return;

    return {
      stages: [{ id: API_ENDPOINTS.STAGE(stageId) }],
      category: category.category,
    };
  }, [stageId, category]);

  const buttons = useMemo(
    (): IFormButtons => ({
      primary: {
        "data-testid": `create_equipment_${category.category}`,
        children: createEquipmentCopy.cta,
        size: "m",
      },
      secondary: {
        onClick: closeForm,
        size: "m",
      },
    }),
    [closeForm, category.category]
  );

  const fetchEquipment = () => {
    if (!stageId) {
      return;
    }

    dispatchGetStageEquipmentAction(dispatch, {
      venue: venueId,
      stage: stageId,
      category: category.category,
      page: 1,
    });
  };

  return (
    <Card theme={ICardTheme.secondary} size="m">
      <Spacer size="m">
        <Title asComponent="h4" title={`${addEquipment.title} ${category.label}`} size="s" />
        <CreateEquipment
          defaultEquipmentValues={defaultEquipmentValues}
          hideCategories
          disableStages={stageId ? [stageId] : []}
          fetchEquipment={fetchEquipment}
          buttons={buttons}
        />
      </Spacer>
    </Card>
  );
};
